import { useState } from 'react';
import { ChevronDown, ChevronUp, Clock, Database, Zap, AlertCircle, CheckCircle, Copy, MapPin } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { CalculationLog } from '@/types/winchStartPoint';

/**
 * CalculationLogs Component
 * Displays detailed calculation metadata in an expandable section
 * Shows distance source, method, cache status, response time, and accuracy
 */

interface CalculationLogsProps {
  log: CalculationLog;
  className?: string;
}

export function CalculationLogs({ log, className = '' }: CalculationLogsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine color scheme based on method
  const getMethodColor = () => {
    if (log.method.includes('google_maps_api')) {
      return {
        bg: 'bg-success/10',
        border: 'border-success/30',
        text: 'text-success',
        icon: '✓',
      };
    } else if (log.method.includes('fallback')) {
      return {
        bg: 'bg-warning/10',
        border: 'border-warning/30',
        text: 'text-warning',
        icon: '⚠',
      };
    }
    return {
      bg: 'bg-info/10',
      border: 'border-info/30',
      text: 'text-info',
      icon: 'ℹ',
    };
  };

  const colors = getMethodColor();

  const copyLogsToClipboard = () => {
    const logsText = `
تفاصيل الحساب - Calculation Details
=====================================
التوقيت: ${new Date(log.timestamp).toLocaleString('ar-EG')}
طريقة الحساب: ${log.distanceSource}
حالة الذاكرة المؤقتة: ${log.cacheStatus === 'hit' ? 'موجود (Cache Hit)' : 'جديد (Cache Miss)'}
وقت الاستجابة: ${log.responseTime}
عدد استدعاءات API: ${log.apiCalls}
الدقة: ${log.accuracy === 'precise' ? 'دقيق (Precise)' : 'تقديري (Estimated)'}
${log.fallbackReason ? `سبب البديل: ${log.fallbackReason}` : ''}

الإحداثيات:
- نقطة البداية: ${log.coordinates.startPoint.lat.toFixed(6)}, ${log.coordinates.startPoint.lng.toFixed(6)}
- الاستلام: ${log.coordinates.pickup.lat.toFixed(6)}, ${log.coordinates.pickup.lng.toFixed(6)}
- الوجهة: ${log.coordinates.destination.lat.toFixed(6)}, ${log.coordinates.destination.lng.toFixed(6)}
    `.trim();

    navigator.clipboard.writeText(logsText)
      .then(() => toast.success('تم نسخ السجلات إلى الحافظة'))
      .catch(() => toast.error('فشل نسخ السجلات'));
  };

  const getMethodLabel = () => {
    if (log.method === 'google_maps_api') return 'Google Maps API';
    if (log.method === 'google_maps_api_cached') return 'Google Maps API (Cached)';
    if (log.method === 'haversine_fallback') return 'Haversine Fallback';
    return log.method;
  };

  return (
    <div className={`card ${className}`} dir="rtl">
      {/* Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full p-4 flex items-center justify-between ${colors.bg} border ${colors.border} rounded-lg transition-all hover:bg-opacity-80`}
      >
        <div className="flex items-center gap-3">
          <div className={`text-2xl ${colors.text}`}>{colors.icon}</div>
          <div className="text-right">
            <h3 className="font-bold text-white">سجل الحسابات</h3>
            <p className="text-sm text-gray-400">{log.distanceSource}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${colors.bg} border ${colors.border}`}>
            <Clock className={`h-4 w-4 ${colors.text}`} />
            <span className={`text-sm font-semibold ${colors.text}`} dir="ltr">
              {log.responseTime}
            </span>
          </div>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Quick Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-dark-lighter rounded-lg border border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="h-4 w-4 text-primary" />
                <span className="text-xs text-gray-400">الطريقة</span>
              </div>
              <p className="text-sm font-semibold text-white">{getMethodLabel()}</p>
            </div>

            <div className="p-3 bg-dark-lighter rounded-lg border border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <Database className="h-4 w-4 text-info" />
                <span className="text-xs text-gray-400">الذاكرة المؤقتة</span>
              </div>
              <p className="text-sm font-semibold text-white">
                {log.cacheStatus === 'hit' ? (
                  <span className="text-success">✓ Cache Hit</span>
                ) : (
                  <span className="text-warning">○ Cache Miss</span>
                )}
              </p>
            </div>

            <div className="p-3 bg-dark-lighter rounded-lg border border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-primary" />
                <span className="text-xs text-gray-400">الوقت</span>
              </div>
              <p className="text-sm font-semibold text-primary" dir="ltr">
                {log.responseTime}
              </p>
            </div>

            <div className="p-3 bg-dark-lighter rounded-lg border border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                {log.accuracy === 'precise' ? (
                  <CheckCircle className="h-4 w-4 text-success" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-warning" />
                )}
                <span className="text-xs text-gray-400">الدقة</span>
              </div>
              <p className="text-sm font-semibold text-white">
                {log.accuracy === 'precise' ? 'دقيق' : 'تقديري'}
              </p>
            </div>
          </div>

          {/* Detailed Information */}
          <div className="space-y-3">
            {/* Timestamp */}
            <div className="p-3 bg-dark-lighter rounded-lg border border-gray-700">
              <p className="text-xs text-gray-400 mb-1">التوقيت</p>
              <p className="text-sm font-mono text-white" dir="ltr">
                {new Date(log.timestamp).toLocaleString('ar-EG', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </p>
            </div>

            {/* API Calls */}
            <div className="p-3 bg-dark-lighter rounded-lg border border-gray-700">
              <p className="text-xs text-gray-400 mb-1">عدد استدعاءات API</p>
              <p className="text-sm font-semibold text-white">
                {log.apiCalls === 0 ? (
                  <span className="text-info">0 (من الذاكرة المؤقتة)</span>
                ) : (
                  <span>{log.apiCalls}</span>
                )}
              </p>
            </div>

            {/* Fallback Reason */}
            {log.fallbackReason && (
              <div className="p-3 bg-warning/10 rounded-lg border border-warning/30">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-warning" />
                  <p className="text-sm font-semibold text-warning">سبب استخدام البديل</p>
                </div>
                <p className="text-sm text-gray-300">{log.fallbackReason}</p>
                <p className="text-xs text-gray-400 mt-1">
                  تم استخدام صيغة Haversine لحساب المسافة التقديرية
                </p>
              </div>
            )}

            {/* Coordinates */}
            <div className="p-4 bg-dark-lighter rounded-lg border border-gray-700">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="h-4 w-4 text-primary" />
                <h4 className="font-semibold text-white">الإحداثيات المستخدمة</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center p-2 bg-dark rounded">
                  <span className="text-sm text-gray-400">نقطة البداية</span>
                  <span className="text-xs font-mono text-primary" dir="ltr">
                    {log.coordinates.startPoint.lat.toFixed(6)}, {log.coordinates.startPoint.lng.toFixed(6)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-2 bg-dark rounded">
                  <span className="text-sm text-gray-400">منطقة الاستلام</span>
                  <span className="text-xs font-mono text-success" dir="ltr">
                    {log.coordinates.pickup.lat.toFixed(6)}, {log.coordinates.pickup.lng.toFixed(6)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-2 bg-dark rounded">
                  <span className="text-sm text-gray-400">منطقة الوجهة</span>
                  <span className="text-xs font-mono text-warning" dir="ltr">
                    {log.coordinates.destination.lat.toFixed(6)}, {log.coordinates.destination.lng.toFixed(6)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Copy Button */}
          <div className="flex justify-end pt-2 border-t border-gray-700">
            <button
              onClick={copyLogsToClipboard}
              className="flex items-center gap-2 px-4 py-2 bg-dark-lighter hover:bg-primary/20 text-gray-300 hover:text-primary rounded-lg transition-all border border-gray-700 hover:border-primary"
            >
              <Copy className="h-4 w-4" />
              <span className="text-sm font-medium">نسخ السجلات</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
